//External libs
import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Subscription } from 'rxjs';

//Local libs
import Service from 'src/app/core/services/service';

/**
 * @service ObservationLogService
 * @description Service for managing observation logs within the aquaculture system.
 * Observation logs capture various daily checks and visual inspections performed at farm sites,
 * such as water color, presence of pests, or general pond conditions.
 *
 * This service acts as the bridge between the frontend and the `/blubay/observationlog/` backend endpoint.
 * It allows the application to retrieve historical observation data and daily summaries.
 *
 * **Key Responsibilities:**
 * 1. **Data Retrieval**: Fetching comprehensive lists of observation activities.
 * 2. **Daily Summaries**: Retrieving counts of observations performed "today" for specific sites.
 * 3. **Resource Management**: Handling HTTP request lifecycles to prevent memory leaks.
 *
 * @feature 1. Retrieve full history of observation logs.
 * @feature 2. Get daily observation counts for dashboard summaries.
 *
 * @architecture
 * Extends the base `Service<ObservationLogModel>` class.
 * Utilizes Angular's `HttpClient` for RESTful communication.
 *
 * @service-endpoint /blubay/observationlog/
 * @service-provider root (singleton, application-wide)
 *
 * @author AquaCloud Development Team
 * @version 1.0.0
 */
@Injectable({
  providedIn: 'root'
})
export class ObservationLogService extends Service<any> {

  /**
   * @type {Array<Subscription>}
   * @description Array tracking all ongoing HTTP request subscriptions.
   * Used to maintain references to active Observables so they can be unsubscribed
   * when the service is destroyed or when cancellation is requested.
   * @default []
   * @side-effect Used to store subscription references for cancellation via cancelOnGoingHttpRequests()
   */
  public ongoingRequests: Subscription[] = [];

  /**
   * @method constructor
   * @description Service constructor with dependency injection of HttpClient.
   *
   * Initializes the service by calling the parent Service constructor with the
   * HttpClient instance and the specific API endpoint path (`/blubay/observationlog/`).
   *
   * @param {HttpClient} httpClient - @service Angular HTTP client for making HTTP requests to the backend.
   * @returns {void}
   */
  constructor(public httpClient: HttpClient) {
    super(httpClient, '/blubay/observationlog/');
  }

  /**
   * @method getAllObservationLogActivities
   * @async
   * @description Fetch all observation log activities from the backend.
   *
   * This method performs a GET request to retrieve a large dataset (pageSize=20000)
   * of observation logs, ordered by creation date ('new').
   *
   * @returns {Promise<Array<ObservationLogModel>>} Promise resolving to an array of ObservationLogModel objects.
   * @throws {HttpErrorResponse} Rejects the promise if the HTTP request fails.
   * @side-effect
   * - Initiates an HTTP GET request.
   * - Pushes the subscription to `ongoingRequests`.
   *
   * @example
   * observationLogService.getAllObservationLogActivities().then(logs => {
   *   console.log('Observation Logs:', logs);
   * });
   */
  public getAllObservationLogActivities() {
    return new Promise<Array<unknown>>((resolve, reject) => {
      const request = this.httpClient.get<Array<unknown>>(`${this.baseUrl}${this.serviceUrl}?pageSize=20000&order=new`, this.httpOptions).subscribe(response => {
        resolve(response);
      }, (error: HttpErrorResponse) => {
        reject(error);
      });
      this.ongoingRequests.push(request);
    });
  }
  
  //To Cancel the onGoing Http Requests
  /**
   * @method cancelOnGoingHttpRequests
   * @description Cancel all ongoing/pending HTTP requests to prevent memory leaks.
   *
   * This method iterates through the `ongoingRequests` array and calls `unsubscribe()`
   * on each subscription. This is typically called when a component using this service
   * is destroyed (ngOnDestroy).
   *
   * @returns {void}
   * @side-effect
   * - Aborts all pending HTTP requests tracked in `ongoingRequests`.
   * - Resets `ongoingRequests` to an empty array.
   */
  public cancelOnGoingHttpRequests(): void {
    this.ongoingRequests.forEach((request) => {
      if (request && typeof request.unsubscribe === 'function') {
        request.unsubscribe();
      }
    });
    this.ongoingRequests = [];
  }
}
